import React, { useRef, useState } from 'react';
import { Button, Flex, Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { CustomButton, CustomInput } from '@/components';

interface SaveLocationModalProps {
  open: boolean;
  address: string;
  onSave: (locationName: string) => Promise<void>;
  onCancel: () => void;
}

const SaveLocationModal: React.FC<SaveLocationModalProps> = ({
  open,
  address,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleSave = async () => {
    if (isSubmittingRef.current) return;
    if (!name.trim()) {
      setError('Location name is required.');
      return;
    }
    isSubmittingRef.current = true;
    setLoading(true);
    try {
      await onSave(name.trim());
      setName('');
      setError('');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCancel = () => {
    setName('');
    setError('');
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      centered
      zIndex={2000}
      width={480}
      className='custom-delete-modal'
    >
      <Flex
        justify='space-between'
        align='center'
        style={{ marginBottom: '12px' }}
      >
        <h3 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>
          Save location
        </h3>
        <CloseOutlined
          style={{ fontSize: 16, cursor: 'pointer', alignSelf: 'flex-start' }}
          onClick={handleCancel}
        />
      </Flex>

      <div style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>
        Save frequently used locations.
      </div>

      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>
        {address}
      </div>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
        Location name
      </div>
      <CustomInput
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setName(e.target.value);
          if (error) setError('');
        }}
        placeholder='e.g. Warehouse A'
        onPressEnter={handleSave}
        height='40px'
        borderRadius='8px'
        status={error ? 'error' : ''}
      />
      {error && (
        <p style={{ fontSize: 12, color: 'var(--danger)', margin: '4px 0 0' }}>
          {error}
        </p>
      )}

      <Flex justify='end' gap={12} style={{ marginTop: '24px' }}>
        <Button
          type='text'
          onClick={handleCancel}
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            height: 40,
          }}
        >
          Cancel
        </Button>
        <CustomButton
          isFormValid={loading}
          text='Save'
          onClick={handleSave}
          height={40}
          fontSize='14px'
          fontWeight={600}
          bgColor='#000'
          color='#fff'
          borderRadius='8px'
          padding='10px 20px'
        />
      </Flex>
    </Modal>
  );
};

export default SaveLocationModal;
